import { Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { ExploreTemplates } from "@/components/elements/ProfileSelector/ExploreTemplates";
import { useCreateProfile } from "@/hooks/queries/useCreateProfile";
import { useElements } from "@/hooks/useElementsProvider";
import { useUser } from "@/hooks/useUserProvider";
import { GridOne } from "@/lib/templates";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";
import {
  markOnboardingCompleted,
  OnboardingUseCase,
  USE_CASE_CATEGORY_ID,
  USE_CASE_OPTIONS,
} from "@/lib/onboarding";

export const CreateDefaultProfileModal = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { user, syncUser } = useUser();
  const { syncProfiles } = useElements();
  const { mutateAsync: createProfile } = useCreateProfile();

  // "question" = pick a use case; "gallery" = the real Explore Templates
  // gallery, narrowed to the chosen category.
  const [step, setStep] = useState<"question" | "gallery">("question");
  const [useCase, setUseCase] = useState<OnboardingUseCase | null>(null);
  const [startingBlank, setStartingBlank] = useState(false);

  useEffect(() => {
    if (open && user?.new_user) {
      captureEvent("onboarding_template_gallery_shown", { source: "new_user" });
    }
    if (!open) {
      setStep("question");
      setUseCase(null);
    }
  }, [open, user?.new_user]);

  const finish = () => {
    markOnboardingCompleted();
    onOpenChange(false);
  };

  const handleAnswer = (value: OnboardingUseCase) => {
    setUseCase(value);
    setStep("gallery");
    captureEvent("onboarding_use_case_answered", { useCase: value });
  };

  const handleTemplateUsed = (template: { id: string; name: string }) => {
    captureEvent("onboarding_template_chosen", {
      templateId: template.id,
      templateName: template.name,
      useCase: useCase ?? "unknown",
      recommended: false,
    });
    finish();
  };

  const handleStartBlank = async () => {
    setStartingBlank(true);
    try {
      captureEvent("onboarding_start_blank_chosen", { source: "new_user" });
      const {
        data: { id },
      } = await createProfile({
        name: "Home",
        elements: Object.values(GridOne()),
      });
      captureEvent("dashboard_profile_created", {
        profileId: id,
        profileName: "Home",
        source: "auto_new_user",
      });
      await syncProfiles();
      await syncUser();
      finish();
    } catch (err) {
      captureEvent("api_mutation_failed", {
        mutationKey: '["createProfile"]',
        reason: sanitizeErrorReason(err),
      });
      toast.error(
        err instanceof Error ? err.message : "Failed to create dashboard.",
      );
    } finally {
      setStartingBlank(false);
    }
  };

  return (
    <>
      <Dialog open={open && step === "question"}>
        <DialogContent disableClose className="max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Welcome to UniDeck</DialogTitle>
            <DialogDescription>
              What will you use UniDeck for? We&apos;ll show you templates to get
              started.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 flex flex-col gap-3">
            <div className="grid gap-3 sm:grid-cols-3">
              {USE_CASE_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  disabled={startingBlank}
                  onClick={() => handleAnswer(option.value)}
                  className="flex flex-col gap-1 rounded-xl border border-border/60 bg-card/60 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:bg-card"
                >
                  <span className="text-base font-semibold">{option.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {option.description}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex justify-center pt-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={startingBlank}
                onClick={handleStartBlank}
                className="text-muted-foreground hover:text-foreground"
              >
                {startingBlank ? (
                  <Loader2Icon className="size-4 animate-spin" />
                ) : null}
                Start blank instead
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ExploreTemplates
        hideTrigger
        open={open && step === "gallery"}
        initialCategoryId={useCase ? USE_CASE_CATEGORY_ID[useCase] : undefined}
        onOpenChange={nextOpen => {
          // Closing the gallery without choosing a template returns to the
          // question so the user still has "Start blank" and other use cases.
          if (!nextOpen) {
            setStep("question");
          }
        }}
        onTemplateUsed={handleTemplateUsed}
      />
    </>
  );
};
